import React, { useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import {
  selectSortDescending,
  toggleSortPDFs,
  selectMergeFileName,
  setMergeFileName,
  selectFilesToMerge,
  setErrorMessage,
  clearErrorMessage,
} from '../pdf-tool-manage/pdf-slice';
import { PathUtils } from '../utils';

import { useAppDispatch } from '../store/store';
import { degrees, PDFDocument } from 'pdf-lib';
import { selectBaseUrl } from '../dff/dff-slice';
import _ from 'lodash';

require('./pdf-tool-merge-panel.scss');

export const MergePanelComponent: React.FC<{}> = () => {
  const [description, setDescription] = useState(
    'To change the order of your PDFs, drag and drop the files as you want.',
  );
  const [destFileUrl, setDestFileUrl] = useState('');
  const [destFileName, setDestFileName] = useState('');
  const [buttonText, setButtonText] = useState('Merge PDF');

  const sortDescending = useSelector(selectSortDescending);
  const filesToMerge = useSelector(selectFilesToMerge);
  const mergeFileName = useSelector(selectMergeFileName);
  const baseUrl = useSelector(selectBaseUrl);

  const dispatch = useAppDispatch();

  const originalUrl = (file: any, finalName: string): string => {
    return `${baseUrl}/app/storage/${
      file.mountId
    }?path=/&name=${encodeURIComponent(finalName)}`;
  };

  const uploadBlob = (blob, fileName, mountId) => {
    return fetch(
      `/content/api/v2/mounts/${mountId}/files/put?path=/&filename=${fileName}&info=true`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: blob,
      },
    )
      .then((response) => response.json())
      .then((r) => {
        setDestFileName(r.name);
        return r;
      });
  };

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    setButtonText('Merging PDFs');
    const sorted = _.sortBy(filesToMerge, (x) => x.order);

    let pdfs: { pdf: PDFDocument; rotation: number; fileName: string }[];
    try {
      pdfs = await Promise.all(
        sorted.map(async (file) => {
          try {
            const url = `/content/api/v2/mounts/${
              file.mountId
            }/files/get?path=${encodeURIComponent(file.path)}`;

            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Failed to fetch ${file.file.name}: ${response.statusText}`);
            }

            const buffer = await response.arrayBuffer();
            const b = new Uint8Array(buffer);

            const pdfDocParsed = await PDFDocument.load(b, {
              ignoreEncryption: true,
              throwOnInvalidObject: false
            });

            return {
              pdf: pdfDocParsed,
              rotation: file.rotateDegrees,
              fileName: file.file.name,
            };
          } catch (error) {
            // Provide specific guidance based on error type
            let errorMessage = `Failed to load PDF "${file.file.name}". `;

            if (error.message?.includes('password') || error.message?.includes('encrypted')) {
              errorMessage += 'This PDF is password-protected. Please remove the password before merging.';
            } else if (error.message?.includes('PDFDict') || error.message?.includes('Unexpected')) {
              errorMessage += 'This PDF appears to be corrupted or malformed. Try opening and re-saving it in a PDF viewer.';
            } else {
              errorMessage += 'The file may be corrupted or use unsupported PDF features.';
            }

            dispatch(setErrorMessage(errorMessage));

            setButtonText('Merge PDF');
            throw error;
          }
        }),
      );
    } catch (error) {
      return;
    }

    try {
      const pdfDoc = await PDFDocument.create();
      for (let i = 0; i < pdfs.length; i++) {
        const element = pdfs[i].pdf;
        const rotation = pdfs[i].rotation;
        const fileName = pdfs[i].fileName;

        try {
          const copiedPages = await pdfDoc.copyPages(
            element,
            element.getPageIndices(),
          );
          copiedPages.forEach((page) => {
            page.setRotation(degrees(rotation));
            pdfDoc.addPage(page);
          });
        } catch (error) {
          dispatch(setErrorMessage(`Failed to merge "${fileName}". This PDF has structural issues.`));

          setButtonText('Merge PDF');
          throw error;
        }
      }

      dispatch(clearErrorMessage());

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });

      uploadBlob(blob, `${mergeFileName}.pdf`, filesToMerge[0].mountId).then(
        (r) => {
          setDescription(`Merged file is available here:`);
          setDestFileUrl(originalUrl(filesToMerge[0], r.name));
          setButtonText('Merge PDF');
        },
      );
    } catch (error) {
      console.error('Merge operation failed:', error);
      setButtonText('Merge PDF');
    }
  };

  const handleSort = (e: React.MouseEvent<HTMLButtonElement>) => {
    dispatch(toggleSortPDFs());
  };

  const handleFileNameChange = (e) => {
    if (!e.currentTarget.value) {
      dispatch(setMergeFileName('merged'));
    } else {
      dispatch(setMergeFileName(e.currentTarget.value));
    }
  };

  return (
    <div className="l-merge-panel__container">
      <div className="l-merge-panel__sort-button-container">
        <button
          className="l-merge-panel__sort-button"
          onClick={(e) => handleSort(e)}
        >
          <span className="l-merge-panel__sort-text">Sort</span>
          <span
            className={classNames('l-merge-panel__sort-image', {
              'l-merge-panel__sort-image--ascending': !sortDescending,
            })}
          ></span>
        </button>
      </div>

      <div className="l-merge-panel__description">{description}</div>
      {destFileUrl ? (
        <a
          href={destFileUrl}
          target="_blank"
          className="l-merge-panel__file-link"
        >
          {destFileName}
        </a>
      ) : (
        <div className="l-merge-panel__input-container">
          <div className="l-merge-panel__label">File name</div>
          <input
            type="text"
            name="fileName"
            id="fileName"
            className="l-merge-panel__input"
            value={mergeFileName}
            onChange={(e) => handleFileNameChange(e)}
          />
          <span className="l-merge-panel__input-help-text">.pdf</span>
        </div>
      )}
      <button
        className="l-merge-panel__split-button"
        onClick={(e) => handleClick(e)}
      >
        {buttonText}
      </button>
    </div>
  );
};
